import React, { useMemo } from 'react';
import { AutocompleteInput } from 'react-admin';
import { useTranslate } from 'react-admin';
import { getAllCities } from '../helpers/citySelector';

interface CityAutocompleteInputProps {
  source: string;
  label?: string;
  fullWidth?: boolean;
  helperText?: string | false;
  validate?: any;
  format?: (value: any) => any;
  parse?: (value: any) => any;
  onChange?: (event: any) => void;
  onBlur?: (event: any) => void;
}

export const CityAutocompleteInput: React.FC<CityAutocompleteInputProps> = ({
  source,
  label,
  fullWidth = true,
  helperText,
  validate,
  format,
  parse,
  onChange,
  onBlur,
  ...rest
}) => {
  const translate = useTranslate();
  
  // Get all cities and format them for AutocompleteInput
  const cityChoices = useMemo(() => {
    const allCities = getAllCities();
    return allCities.map(city => ({
      id: city.id, // Use unique ID (stateId-cityName) as the value
      name: city.displayName, // Use "State Name - City Name" as display text
      cityName: city.name, // Store the actual city name for reference
    }));
  }, []);

  // Custom filter function to search by both city name and state name
  const matchSuggestion = (filter: string, choice: { id: string; name: string; cityName: string }) => {
    const lowercaseFilter = filter.toLowerCase();
    return (
      choice.name.toLowerCase().includes(lowercaseFilter) ||
      choice.cityName.toLowerCase().includes(lowercaseFilter)
    );
  };

  // Function to get the display text for the input
  const inputText = (choice: { id: string; name: string; cityName: string }) => {
    return choice ? choice.name : '';
  };

  // Function to parse the selected value to store the full "State - City" format
  const parseValue = (value: string) => {
    if (!value) return '';
    // Find the choice that matches the value and return the full display name
    const choice = cityChoices.find(c => c.id === value);
    return choice ? choice.name : value;
  };

  // Function to format the form value to find the corresponding choice
  const formatValue = (value: string) => {
    if (!value) return '';
    // Find the choice that has this "State - City" format and return its ID
    const choice = cityChoices.find(c => c.name === value);
    return choice ? choice.id : value;
  };

  return (
    <AutocompleteInput
      source={source}
      label={label || translate('citySearch.label')}
      choices={cityChoices}
      matchSuggestion={matchSuggestion}
      inputText={inputText}
      fullWidth={fullWidth}
      helperText={helperText}
      validate={validate}
      format={format || formatValue}
      parse={parse || parseValue}
      onChange={onChange}
      onBlur={onBlur}
      TextFieldProps={{
        placeholder: translate('citySearch.placeholder'),
      }}
      noOptionsText={translate('citySearch.noResults')}
      {...rest}
    />
  );
};
